import { HttpErrorResponse } from '@angular/common/http';

import { StatusCodes } from 'http-status-codes';

/**
 * Reads the HTTP status from anything a KT API call can reject with.
 *
 * The generated hey-api client throws the response **body** (a ProblemDetails `{ status }` object),
 * not an `HttpErrorResponse`, and Angular's `resource()` wraps any non-Error rejection in a
 * `ResourceWrappedError` that keeps the original payload in `cause`. So besides the two direct
 * shapes, an `Error`'s `cause` is inspected exactly once — never walked deeper.
 *
 * @param error The rejection to inspect.
 * @returns The HTTP status code, or `undefined` when none can be read.
 */
export function httpErrorStatus(error: unknown): number | undefined {
  const status = ownStatus(error);
  if (status !== undefined) {
    return status;
  }

  return error instanceof Error ? ownStatus(error.cause) : undefined;
}

/**
 * Tells a failure worth retrying (no response at all, or a 5xx) from a final answer of the server.
 *
 * @param error The rejection to inspect.
 * @returns Whether the request may succeed when repeated unchanged.
 */
export function isTransientHttpError(error: unknown): boolean {
  const status = httpErrorStatus(error);
  return status === undefined || status === 0 || status >= Number(StatusCodes.INTERNAL_SERVER_ERROR);
}

/**
 * Reads the status carried directly by an `HttpErrorResponse` or a `{ status: number }` object.
 *
 * @param error The value to inspect.
 * @returns The HTTP status code, or `undefined` when the value carries none.
 */
function ownStatus(error: unknown): number | undefined {
  if (error instanceof HttpErrorResponse) {
    return error.status;
  }

  if (typeof error === 'object' && error !== null && 'status' in error) {
    const { status } = error;
    return typeof status === 'number' ? status : undefined;
  }

  return undefined;
}
